import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { useTranslation } from "react-i18next";
import { RotateCcw, ChevronsUpDown, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { SPEECH_MODEL_PRICING, calculateSpeechCredits } from "@/config/masterPricing";

export interface SpeechSettings {
  model: string;
  language: string;
  voice_id: string;
  emotion: string;
}

interface SpeechQuickSettingsProps {
  settings: SpeechSettings;
  onSettingsChange: (settings: SpeechSettings) => void;
  hasChangedSettings: boolean;
  onUseDefaults: () => void;
  textLength?: number;
}

// Languages for Minimax (premium) - full list
const MINIMAX_LANGUAGES = [
  { id: "ar", name: "Arabic" },
  { id: "bn", name: "Bengali" },
  { id: "bg", name: "Bulgarian" },
  { id: "zh", name: "Chinese" },
  { id: "hr", name: "Croatian" },
  { id: "cs", name: "Czech" },
  { id: "da", name: "Danish" },
  { id: "nl", name: "Dutch" },
  { id: "en", name: "English" },
  { id: "et", name: "Estonian" },
  { id: "fi", name: "Finnish" },
  { id: "fr", name: "French" },
  { id: "de", name: "German" },
  { id: "el", name: "Greek" },
  { id: "he", name: "Hebrew" },
  { id: "hi", name: "Hindi" },
  { id: "hu", name: "Hungarian" },
  { id: "id", name: "Indonesian" },
  { id: "it", name: "Italian" },
  { id: "ja", name: "Japanese" },
  { id: "ko", name: "Korean" },
  { id: "lv", name: "Latvian" },
  { id: "lt", name: "Lithuanian" },
  { id: "ms", name: "Malay" },
  { id: "no", name: "Norwegian" },
  { id: "fa", name: "Persian" },
  { id: "pl", name: "Polish" },
  { id: "pt", name: "Portuguese" },
  { id: "ro", name: "Romanian" },
  { id: "ru", name: "Russian" },
  { id: "sr", name: "Serbian" },
  { id: "sk", name: "Slovak" },
  { id: "sl", name: "Slovenian" },
  { id: "es", name: "Spanish" },
  { id: "sv", name: "Swedish" },
  { id: "th", name: "Thai" },
  { id: "tr", name: "Turkish" },
  { id: "uk", name: "Ukrainian" },
  { id: "vi", name: "Vietnamese" },
];

// Languages for Kokoro (budget) - limited list
const KOKORO_LANGUAGES = [
  { id: "en", name: "English" },
  { id: "fr", name: "French" },
  { id: "hi", name: "Hindi" },
  { id: "it", name: "Italian" },
  { id: "ja", name: "Japanese" },
  { id: "zh", name: "Chinese" },
];

// Voices for Minimax (organized by language and gender)
// Universal voices for Minimax (work with any language via language_boost)
const MINIMAX_VOICES: { id: string; name: string; gender: "female" | "male" }[] = [
  // Female voices
  { id: "Wise_Woman", name: "Wise Woman", gender: "female" },
  { id: "Calm_Woman", name: "Calm Woman", gender: "female" },
  { id: "Inspirational_girl", name: "Inspirational Girl", gender: "female" },
  { id: "Lively_Girl", name: "Lively Girl", gender: "female" },
  { id: "Lovely_Girl", name: "Lovely Girl", gender: "female" },
  { id: "Sweet_Girl_2", name: "Sweet Girl", gender: "female" },
  { id: "Exuberant_Girl", name: "Exuberant Girl", gender: "female" },
  { id: "Abbess", name: "Abbess", gender: "female" },
  // Male voices
  { id: "Deep_Voice_Man", name: "Deep Voice Man", gender: "male" },
  { id: "Friendly_Person", name: "Friendly Person", gender: "male" },
  { id: "Casual_Guy", name: "Casual Guy", gender: "male" },
  { id: "Patient_Man", name: "Patient Man", gender: "male" },
  { id: "Young_Knight", name: "Young Knight", gender: "male" },
  { id: "Determined_Man", name: "Determined Man", gender: "male" },
  { id: "Decent_Boy", name: "Decent Boy", gender: "male" },
  { id: "Imposing_Manner", name: "Imposing Manner", gender: "male" },
  { id: "Elegant_Man", name: "Elegant Man", gender: "male" },
];

// Voices for Kokoro (from documentation)
const KOKORO_VOICES: Record<string, { id: string; name: string; gender: "female" | "male"; grade?: string }[]> = {
  en: [
    // American English - Female
    { id: "af_bella", name: "Bella", gender: "female", grade: "A-" },
    { id: "af_nicole", name: "Nicole", gender: "female", grade: "B-" },
    { id: "af_aoede", name: "Aoede", gender: "female", grade: "C+" },
    { id: "af_kore", name: "Kore", gender: "female", grade: "C+" },
    { id: "af_sarah", name: "Sarah", gender: "female", grade: "C+" },
    // American English - Male
    { id: "am_michael", name: "Michael", gender: "male", grade: "C+" },
    { id: "am_fenrir", name: "Fenrir", gender: "male", grade: "C+" },
    { id: "am_adam", name: "Adam", gender: "male", grade: "F+" },
    // British English - Female
    { id: "bf_emma", name: "Emma (British)", gender: "female", grade: "B-" },
    { id: "bf_isabella", name: "Isabella (British)", gender: "female", grade: "C" },
    // British English - Male
    { id: "bm_george", name: "George (British)", gender: "male", grade: "C" },
    { id: "bm_lewis", name: "Lewis (British)", gender: "male", grade: "D+" },
  ],
  fr: [
    { id: "ff_siwis", name: "Siwis", gender: "female", grade: "B-" },
  ],
  hi: [
    { id: "hf_alpha", name: "Alpha", gender: "female", grade: "C" },
    { id: "hf_beta", name: "Beta", gender: "female", grade: "C" },
    { id: "hm_omega", name: "Omega", gender: "male", grade: "C" },
    { id: "hm_psi", name: "Psi", gender: "male", grade: "C" },
  ],
  it: [
    { id: "if_sara", name: "Sara", gender: "female", grade: "C" },
    { id: "im_nicola", name: "Nicola", gender: "male", grade: "C" },
  ],
  ja: [
    { id: "jf_alpha", name: "Alpha", gender: "female", grade: "C+" },
    { id: "jf_gongitsune", name: "Gongitsune", gender: "female", grade: "C" },
    { id: "jm_kumo", name: "Kumo", gender: "male", grade: "C-" },
  ],
  zh: [
    { id: "zf_xiaobei", name: "Xiaobei", gender: "female", grade: "D" },
    { id: "zf_xiaoni", name: "Xiaoni", gender: "female", grade: "D" },
    { id: "zm_yunxi", name: "Yunxi", gender: "male", grade: "D" },
    { id: "zm_yunyang", name: "Yunyang", gender: "male", grade: "D" },
  ],
};

const EMOTIONS = [
  { id: "auto", name: "Auto" },
  { id: "neutral", name: "Neutral" },
  { id: "happy", name: "Happy" },
  { id: "sad", name: "Sad" },
  { id: "angry", name: "Angry" },
  { id: "calm", name: "Calm" },
  { id: "surprised", name: "Surprised" },
];

export const SpeechQuickSettings = ({
  settings,
  onSettingsChange,
  hasChangedSettings,
  onUseDefaults,
  textLength = 0,
}: SpeechQuickSettingsProps) => {
  const { t } = useTranslation();
  const [languageOpen, setLanguageOpen] = useState(false);

  // Get model info
  const currentModel = SPEECH_MODEL_PRICING.find(m => m.id === settings.model) || SPEECH_MODEL_PRICING[0];
  const isKokoro = settings.model === 'kokoro-82m';

  // Get available languages based on model
  const availableLanguages = isKokoro ? KOKORO_LANGUAGES : MINIMAX_LANGUAGES;

  // Get available voices based on model (Minimax voices are universal, Kokoro are per-language)
  const availableVoices = isKokoro 
    ? (KOKORO_VOICES[settings.language] || KOKORO_VOICES["en"] || [])
    : MINIMAX_VOICES;
  const femaleVoices = availableVoices.filter(v => v.gender === "female");
  const maleVoices = availableVoices.filter(v => v.gender === "male");

  // Handle model change - reset language and voice
  const handleModelChange = (modelId: string) => {
    const newIsKokoro = modelId === 'kokoro-82m';
    const newLanguages = newIsKokoro ? KOKORO_LANGUAGES : MINIMAX_LANGUAGES;
    
    // If current language is not supported, default to English
    const newLanguage = newLanguages.find(l => l.id === settings.language) ? settings.language : 'en';
    const newVoices = newIsKokoro 
      ? (KOKORO_VOICES[newLanguage] || KOKORO_VOICES['en'] || [])
      : MINIMAX_VOICES;
    
    onSettingsChange({
      ...settings,
      model: modelId,
      language: newLanguage,
      voice_id: newVoices[0]?.id || 'af_bella',
      emotion: newIsKokoro ? 'auto' : settings.emotion,
    });
  };

  // Handle language change - reset voice to first available
  const handleLanguageChange = (language: string) => {
    const voices = isKokoro 
      ? (KOKORO_VOICES[language] || KOKORO_VOICES["en"] || [])
      : MINIMAX_VOICES;
    onSettingsChange({
      ...settings,
      language,
      voice_id: voices[0]?.id || (isKokoro ? "af_bella" : "Wise_Woman"),
    });
    setLanguageOpen(false);
  };

  // Calculate cost preview
  const estimatedCost = calculateSpeechCredits(textLength, settings.model);
  const maxChars = currentModel.maxChars;

  // Get current language name
  const currentLanguage = availableLanguages.find(l => l.id === settings.language);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3">
        {/* Model Selection */}
        <div className="flex flex-col items-start gap-1">
          <span className="text-xs text-muted-foreground ml-1">{t("speech.model", "Model")}</span>
          <Select value={settings.model} onValueChange={handleModelChange}>
            <SelectTrigger className="w-[140px] md:w-[200px] h-8 md:h-9 bg-muted/50 border-muted text-xs md:text-sm rounded-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel className="text-xs text-muted-foreground">Budget</SelectLabel>
                {SPEECH_MODEL_PRICING.filter(m => m.tier === 'budget').map((model) => (
                  <SelectItem key={model.id} value={model.id}>
                    {model.displayName} ({model.flatCredits} credits)
                  </SelectItem>
                ))}
              </SelectGroup>
              <SelectGroup>
                <SelectLabel className="text-xs text-muted-foreground">Premium</SelectLabel>
                {SPEECH_MODEL_PRICING.filter(m => m.tier === 'premium').map((model) => (
                  <SelectItem key={model.id} value={model.id}>
                    {model.displayName}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        {/* Language Selection - Searchable */}
        <div className="flex flex-col items-start gap-1">
          <span className="text-xs text-muted-foreground ml-1">{t("speech.language", "Language")}</span>
          <Popover open={languageOpen} onOpenChange={setLanguageOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={languageOpen}
                className="w-[110px] md:w-[160px] h-8 md:h-9 justify-between bg-muted/50 border-muted text-xs md:text-sm rounded-full"
              >
                {currentLanguage?.name || "Select language"}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[200px] p-0">
              <Command>
                <CommandInput placeholder="Search language..." />
                <CommandList>
                  <CommandEmpty>No language found.</CommandEmpty>
                  <CommandGroup>
                    {availableLanguages.map((lang) => (
                      <CommandItem
                        key={lang.id}
                        value={lang.name}
                        onSelect={() => handleLanguageChange(lang.id)}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            settings.language === lang.id ? "opacity-100" : "opacity-0"
                          )}
                        />
                        {lang.name}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        {/* Voice Selection - grouped by gender */}
        <div className="flex flex-col items-start gap-1">
          <span className="text-xs text-muted-foreground ml-1">{t("speech.voice", "Voice")}</span>
          <Select
            value={settings.voice_id}
            onValueChange={(value) =>
              onSettingsChange({ ...settings, voice_id: value })
            }
          >
            <SelectTrigger className="w-[120px] md:w-[180px] h-8 md:h-9 bg-muted/50 border-muted text-xs md:text-sm rounded-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {femaleVoices.length > 0 && (
                <SelectGroup>
                  <SelectLabel className="text-xs text-muted-foreground">Female</SelectLabel>
                  {femaleVoices.map((voice) => (
                    <SelectItem key={voice.id} value={voice.id}>
                      {voice.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              )}
              {maleVoices.length > 0 && (
                <SelectGroup>
                  <SelectLabel className="text-xs text-muted-foreground">Male</SelectLabel>
                  {maleVoices.map((voice) => (
                    <SelectItem key={voice.id} value={voice.id}>
                      {voice.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              )}
            </SelectContent>
          </Select>
        </div>

        {/* Emotion Selection - only for Minimax */}
        {!isKokoro && (
          <div className="flex flex-col items-start gap-1">
            <span className="text-xs text-muted-foreground ml-1">{t("speech.emotion", "Emotion")}</span>
            <Select
              value={settings.emotion}
              onValueChange={(value) =>
                onSettingsChange({ ...settings, emotion: value })
              }
            >
              <SelectTrigger className="w-[90px] md:w-[120px] h-8 md:h-9 bg-muted/50 border-muted text-xs md:text-sm rounded-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EMOTIONS.map((emotion) => (
                  <SelectItem key={emotion.id} value={emotion.id}>
                    {emotion.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Reset Button */}
        {hasChangedSettings && (
          <div className="flex flex-col items-start gap-1">
            <span className="text-xs text-transparent ml-1">Reset</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={onUseDefaults}
              className="h-9 px-3 rounded-full text-muted-foreground hover:text-foreground"
            >
              <RotateCcw className="h-4 w-4 mr-1" />
              {t("advancedSettings.useDefaultSettings")}
            </Button>
          </div>
        )}
      </div>

      {/* Dynamic cost preview */}
      <p className="text-center text-xs text-muted-foreground">
        {isKokoro ? (
          textLength > 0 
            ? t("speech.flatCostPreview", "{{cost}} credits (flat rate) • Max {{max}} characters", { 
                cost: estimatedCost.toLocaleString(),
                max: maxChars.toLocaleString()
              })
            : t("speech.flatCostInfo", "{{cost}} credits flat rate (max {{max}} chars)", {
                cost: currentModel.flatCredits?.toLocaleString() || "75",
                max: maxChars.toLocaleString()
              })
        ) : (
          textLength > 0 
            ? t("speech.costPreview", "~{{cost}} credits for {{chars}} characters", { 
                cost: estimatedCost.toLocaleString(), 
                chars: textLength.toLocaleString() 
              })
            : t("speech.costInfo", "0.5 credits per character (min 150)")
        )}
      </p>
    </div>
  );
};
